import {CheckCircle2, Dices} from "lucide-react";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useTurn} from "@/encounter/turns/TurnHooks.ts";
import TokenFramePreview from "@/tabletop/tokens/TokenFramePreview.tsx";
import {tokenFrameStyle} from "@/tabletop/tokens/TokenFrameStyles.ts";

/** Passive player presentation: readiness and counts include only released friendly participants. */
export default function CombatPreparationOverlay() {
    const {combatState, initialLoading} = useTurn();
    const {combatants} = useCombatants();
    if (initialLoading || combatState.phase !== "INITIATIVE") return null;
    const byId = new Map(combatants.map(actor => [actor.id, actor]));
    const friendly = combatState.participants.flatMap(entry => {
        const actor = byId.get(entry.combatantId);
        return actor && !actor.hostile && !actor.hidden ? [{entry, actor}] : [];
    });
    const ready = friendly.filter(({entry}) => entry.initiative != null).length;
    const allReady = friendly.length > 0 && ready === friendly.length;
    return <section aria-label="Kampfvorbereitung"
        className="@container absolute inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/65 p-4 text-white backdrop-blur-sm">
        <div className="my-auto w-full max-w-4xl rounded-2xl border border-white/20 bg-gray-950/95 p-5 shadow-2xl @min-[600px]:p-8">
            <div className="mb-6 text-center">
                <Dices aria-hidden="true" className="mx-auto mb-3 size-10 text-amber-300"/>
                <h2 className="text-2xl font-semibold @min-[600px]:text-3xl">Der Kampf beginnt</h2>
                <p className="mt-2 text-white/80">Würfelt eure Initiative und nennt sie dem Spielleiter.</p>
            </div>
            <ul aria-label="Freundliche Kampfteilnehmer" className="grid grid-cols-1 gap-3 @min-[480px]:grid-cols-2 @min-[760px]:grid-cols-3">
                {friendly.map(({entry, actor}) => {
                    const isReady = entry.initiative != null;
                    return <li key={entry.participationId} data-combatant-id={actor.id}
                        className={`flex items-center gap-3 rounded-xl border p-3 ${isReady ? "border-green-400/40 bg-green-400/5" : "border-white/15 bg-white/5"}`}>
                        <TokenFramePreview style={tokenFrameStyle(actor.tokenFrame)} image={actor.character.image} className="size-14 @min-[760px]:size-16 @min-[1100px]:size-20"/>
                        <div className="min-w-0 flex-1">
                            <h3 className="truncate font-semibold" title={actor.name}>{actor.name}</h3>
                            <p className="mt-1 text-sm">Initiative: <span className="font-semibold tabular-nums">{isReady ? entry.initiative : "–"}</span></p>
                            <p className={`mt-1 flex items-center gap-1.5 text-sm ${isReady ? "text-green-300" : "text-white/65"}`}>
                                {isReady && <CheckCircle2 aria-hidden="true" className="size-4 shrink-0"/>}
                                {isReady ? "Kampfbereit" : "Wartet auf Initiative"}
                            </p>
                        </div>
                    </li>;
                })}
            </ul>
            <div role="status" aria-live="polite" aria-atomic="true" className="mt-6 text-center text-sm text-white/80">
                {friendly.length > 0 && <p>{ready} von {friendly.length} bereit</p>}
                <p className="mt-1">{allReady ? "Bereit – der Spielleiter startet den Kampf" : "Wartet auf den Spielleiter."}</p>
            </div>
        </div>
    </section>;
}
